class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartSmartRecommendations extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    this.load();
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  async load() {
    const signal = this.controller.signal;
    const productIds = JSON.parse(this.dataset.productIds);
    const list = this.querySelector('.cart-drawer__recommendations-list');
    const fallback = Array.from(list.children);
    const seen = new Set(productIds.map(String));
    const items = [];
    const limit = Number(this.dataset.limit);
    for (const productId of productIds) {
      if (signal.aborted || items.length >= limit) break;
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('section_id', 'cart-smart-recommendations');
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        const response = await fetch(url, { signal });
        if (!response.ok) continue;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        html.querySelectorAll('.cart-drawer__recommendation-item').forEach((item) => {
          if (!seen.has(item.dataset.productId) && items.length < limit) {
            seen.add(item.dataset.productId);
            items.push(item);
          }
        });
      } catch (error) {
        if (signal.aborted) return;
      }
    }
    if (signal.aborted || !this.isConnected) return;
    fallback.forEach((item) => {
      if (!seen.has(item.dataset.productId) && items.length < limit) {
        seen.add(item.dataset.productId);
        items.push(item);
      }
    });
    if (!list.contains(document.activeElement)) list.replaceChildren(...items);
    this.closest('.cart-drawer__recommendations').hidden = items.length === 0;
    this.closest('.drawer__inner').classList.toggle('drawer__inner--with-recommendations', items.length > 0);
  }
}

customElements.define('cart-smart-recommendations', CartSmartRecommendations);
